/**
 * MVP-496. "Is the person typing into this element right now?" — asked by every keydown handler in
 * the product that binds a bare key (no Ctrl/Alt/Meta) to an action: the operator hotkey table
 * (`features/ops/hotkeys.ts`), the library's shortcuts, the guest surface's `/` hotkey (MVP-490),
 * its digit pad (MVP-274 R1) and its arrow-key result walk (MVP-491).
 *
 * It lives in `lib/` rather than in `features/ops/hotkeys.ts` because both the operator side and the
 * guest side need it and the guest entry bundle is capped at 150 KB gz (AC-REQ-2): the guest already
 * imports from `lib/` (`lib/deviceId`, `lib/useFullscreen`), so a leaf module here costs it nothing,
 * while pulling in the whole hotkey table would make the guest pay for the operator's bindings.
 *
 * Before this task the guest surface hand-rolled its own copy in three places and the copies had
 * already drifted from the operator's: they omitted `SELECT`. `features/guest/isTypingTarget.test.ts`
 * pins that there is now exactly one definition, by identity — two copies that merely agree today
 * cannot satisfy it.
 */

/** The parts of an element this reads. `HTMLElement` satisfies it; so does a plain object in a test. */
export interface TypingTargetLike {
  tagName?: string;
  isContentEditable?: boolean;
}

/**
 * True when the event's target is a place the user is typing (or otherwise owns the keys) — a
 * hotkey must not fire while someone types a marquee message that happens to contain "n".
 *
 * `SELECT` counts even though nobody "types" into one: a focused `<select>` owns the arrow keys,
 * which is how it is operated from a keyboard at all. An app that steals them moves the page out
 * from under someone who was choosing an option — on the guest surface that is live the moment a
 * `<select>` lands inside `.guest-search`, because MVP-491 bound Up/Down there to walking the
 * result list.
 *
 * `tagName` is upper-cased rather than compared as-is: it is already uppercase for HTML elements in
 * an HTML document, but not for elements from an XML/SVG subtree, and a focusable `<foreignObject>`
 * field is not worth a silent miss.
 */
export function isTypingTarget(target: TypingTargetLike | null | undefined): boolean {
  if (!target) return false;
  const tag = target.tagName?.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || Boolean(target.isContentEditable);
}
